import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import signalService from '../../services/signalService';
import portalService from '../../services/portalService';
import friendService from '../../services/friendService';
import introductionService from '../../services/introductionService';
import proService from '../../services/proService';
import { fillFor, T, offerAmount } from './notificationModel';
import openAppLink from '../../utils/openAppLink';

/**
 * The pills under a row (handoff §1 "Actions"). Every handler stops the row's own click
 * (which would mark-read + navigate) and marks the row read itself; after an action the
 * pills give way to one #F2F2F2 pill saying what happened.
 *
 *   n        a decorated row (notificationModel.decorate)
 *   onRead   (id) → mark it read
 *   onClose  the dropdown closes itself before navigating; the page passes nothing
 */
export default function NotificationActions({ n, onRead, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [acted, setActed] = useState(null);
  const [error, setError] = useState(null);

  if (!n.actionKind) return null;

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  const go = (path) => { onClose?.(); if (path) openAppLink(navigate, path); };
  const run = async (e, fn, doneValue) => {
    stop(e);
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (r && r.success === false) { setError(r.error || 'That didn’t go through.'); return; }
      if (n.unread) onRead?.(n.id);
      setActed(doneValue);
    } finally {
      setBusy(false);
    }
  };
  const open = (e, path = n.actionUrl) => { stop(e); if (n.unread) onRead?.(n.id); go(path); };

  const pill = (label, onClick, kind = null, key = label) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      disabled={busy}
      className="pr-notif-action"
      style={{
        height: 40, padding: '0 18px', border: '2px solid transparent', borderRadius: 999, boxSizing: 'border-box',
        background: kind === 'on' ? fillFor('on') : kind === 'primary' ? T.ink : T.off,
        color: kind ? '#fff' : T.ink,
        fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: busy ? 'default' : 'pointer',
        transition: 'opacity .2s', opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
  const done = (text) => (
    <span
      data-testid="notification-done"
      style={{ alignSelf: 'flex-start', marginTop: 4, padding: '6px 12px', borderRadius: 999, background: T.off, fontSize: 10, fontWeight: 700, letterSpacing: '.12em' }}
    >
      {text}
    </span>
  );
  const wrap = (kids) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>{kids}</div>
      {error && <span style={{ fontSize: 10, letterSpacing: '.08em', color: T.neg }}>{error}</span>}
    </div>
  );

  const portal = n.portal;
  const noir = n.type === 'PortalSignalReceived' || n.type === 'PortalSignalInvited';
  const eventId = portal?.eventId;
  const imDown = () => (noir ? portalService.respondImDown(eventId) : signalService.imDown(eventId));
  const withdraw = () => (noir ? portalService.withdrawResponse(eventId) : signalService.withdraw(eventId));

  switch (n.actionKind) {
    case 'portal': {
      if (portal.isClosed) return done('THIS PORTAL CLOSED.');
      const state = acted || portal.viewerState;
      if (state === 'in') {
        return wrap([
          pill("YOU'RE IN", (e) => run(e, withdraw, 'none'), 'on'),
          pill('OPEN CHAT', (e) => open(e)),
        ]);
      }
      if (state === 'passed') return done('PASSED. THEY WON’T BE TOLD.');
      return wrap([
        pill("I'M IN", (e) => run(e, imDown, 'in'), 'primary'),
        pill('PASS', (e) => run(e, () => signalService.dismiss(eventId), 'passed')),
      ]);
    }
    case 'invite': {
      if (portal.isClosed) return done('THIS PORTAL CLOSED.');
      const state = acted || portal.viewerState;
      if (state === 'in') return wrap([pill("YOU'RE IN", () => {}, 'on'), pill('OPEN CHAT', (e) => open(e))]);
      if (state === 'passed') return done('DECLINED.');
      return wrap([
        pill("I'M IN", (e) => run(e, () => portalService.respondToSignalInvite(eventId, true), 'in'), 'primary'),
        pill('PASS', (e) => run(e, () => portalService.respondToSignalInvite(eventId, false), 'passed')),
      ]);
    }
    case 'friend':
      if (acted) return done(acted === 'ok' ? 'CONNECTED.' : 'DECLINED.');
      return wrap([
        pill('ACCEPT', (e) => run(e, () => friendService.respondToRequest(n.relatedEntityId, true), 'ok'), 'primary'),
        pill('DECLINE', (e) => run(e, () => friendService.respondToRequest(n.relatedEntityId, false), 'no')),
      ]);
    case 'intro':
      if (acted) return done(acted === 'ok' ? 'ACCEPTED. WAITING ON THEM.' : 'DECLINED.');
      return wrap([
        pill('ACCEPT', (e) => run(e, () => introductionService.respond(n.relatedEntityId, true), 'ok'), 'primary'),
        pill('DECLINE', (e) => run(e, () => introductionService.respond(n.relatedEntityId, false), 'no')),
      ]);
    case 'offer': {
      const amount = offerAmount(n.message);
      // A guest buyer's offer lives behind a token link, not an id we can act on here.
      const guestLink = (n.relatedEntityType || '').startsWith('pro_offer_guest:');
      if (acted) return wrap([done(`ACCEPTED${amount ? ` AT ${amount}` : ''}.`), pill('VIEW OFFER', (e) => open(e))]);
      if (guestLink) return wrap([pill(amount ? `REVIEW ${amount}` : 'REVIEW OFFER', (e) => open(e), 'primary')]);
      return wrap([
        pill(amount ? `ACCEPT ${amount}` : 'ACCEPT', (e) => run(e, () => proService.acceptOffer(n.relatedEntityId), 'ok'), 'primary'),
        pill('COUNTER', (e) => open(e)),
      ]);
    }
    case 'spot':
      if (acted) return done('LET GO.');
      return wrap([
        pill('CLAIM SPOT', (e) => open(e), 'primary'),
        pill('LET IT GO', (e) => run(e, async () => ({ success: true }), 'no')),
      ]);
    case 'claim':
      return wrap([pill('CLAIM IT', (e) => open(e, n.actionUrl || '/host/claim'), 'on')]);
    case 'chat':
      return wrap([pill('OPEN CHAT', (e) => open(e))]);
    case 'reply':
      return wrap([pill('REPLY', (e) => open(e))]);
    case 'vote':
      return wrap([pill('VOTE', (e) => open(e))]);
    case 'payout':
      return wrap([pill('FIX PAYOUT', (e) => open(e, '/wallet'), 'primary')]);
    default:
      return null;
  }
}
